<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import {
  dataInsightListLogGroups,
  dataInsightLogAnalytics,
  dataInsightQueryLogs,
  dataInsightSearchLogs,
} from '@/api/cfes/dataInsight'
import { toast } from '@/utils/toast'
import { copyToClipboard } from '@/utils/common'

// ---- time range defaults (last 1 hour, formatted as UTC for datetime-local)
const pad = (n) => String(n).padStart(2, '0')
const toLocalInput = (d) =>
  `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`

const now = new Date()
const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000)

// Default CloudWatch Logs Insights query. Result sizing is handled by paging.
const DEFAULT_QL_FRAGMENT = `fields @timestamp, @logStream, @message
| sort @timestamp desc`

const DEFAULT_ANALYTICS_QUERY = `fields @timestamp, @log, @logStream, @message
| sort @timestamp desc`

// ---- form state ------------------------------------------------------------
const activeTab = ref('ql')
const selectedLogGroups = ref([])
const startDate = ref(toLocalInput(oneHourAgo))
const endDate = ref(toLocalInput(now))
const queryFragment = ref(DEFAULT_QL_FRAGMENT)
const keyword = ref('')

const logGroups = ref([])
const logGroupsLoading = ref(false)

const searchLoading = ref(false)
const visible = ref(false)
const logItems = ref([])
const totalItems = ref(0)
// Server-side pagination state. v-data-table-server expects 1-based page.
const page = ref(1)
const pageSize = ref(25)
// Expanded row ids for v-data-table (Vuetify v3 expects array of item keys)
const expanded = ref([])

// Log Analytics keeps its own query and result state because raw Insights
// queries can return arbitrary columns (for example stats/parse aliases).
const analyticsQuery = ref(DEFAULT_ANALYTICS_QUERY)
const analyticsLoading = ref(false)
const analyticsVisible = ref(false)
const analyticsItems = ref([])
const analyticsTotalItems = ref(0)
const analyticsPage = ref(1)
const analyticsPageSize = ref(25)
const analyticsExpanded = ref([])
const analyticsColumns = ref([])

const analyticsHeaders = computed(() => [
  { title: '', key: 'data-table-expand', sortable: false, width: '36px' },
  ...analyticsColumns.value.map(({ title, key }) => ({
    title,
    key,
    sortable: false,
    minWidth: '160px',
  })),
])

const headers = [
  { title: '', key: 'data-table-expand', sortable: false, width: '36px' },
  { title: 'Time (UTC)', key: 'timestamp', width: '200px', sortable: false },
  { title: 'Level', key: 'level', width: '90px', sortable: false },
  { title: 'Logger', key: 'loggerName', width: '320px', sortable: false },
  { title: 'Thread', key: 'thread', width: '120px', sortable: false },
  { title: 'Message', key: 'message', sortable: false },
]

// ---- helpers ---------------------------------------------------------------
const toEpochMs = (localStr) => {
  if (!localStr) return null
  // datetime-local was formatted as UTC; append Z to parse as UTC
  return new Date(localStr + 'Z').getTime()
}

const formatTs = (input) => {
  if (input == null || input === '') return ''
  let d
  if (typeof input === 'number') {
    // epoch seconds (legacy log4j2 `instant.epochSecond` unpacked by backend)
    d = new Date(input * 1000)
  } else if (typeof input === 'string') {
    // ISO-8601 UTC string from the new `@timestamp` field
    d = new Date(input)
  } else {
    return ''
  }
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} `
    + `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}Z`
}

const levelClass = (level) => {
  const lv = String(level || '').toUpperCase()
  if (lv === 'ERROR' || lv === 'FATAL') return 'level-error'
  if (lv === 'WARN' || lv === 'WARNING') return 'level-warn'
  if (lv === 'INFO') return 'level-info'
  if (lv === 'DEBUG' || lv === 'TRACE') return 'level-debug'
  return ''
}

/**
 * Normalize backend response for log groups.
 * Tolerates Set<String> (bare array) as well as { data | logGroups | items } wrappers.
 */
const normalizeLogGroups = (raw) => {
  const list = Array.isArray(raw)
    ? raw
    : (raw?.data ?? raw?.logGroups ?? raw?.items ?? [])
  const arr = Array.isArray(list) ? list : []
  return arr
    .map((g) => (typeof g === 'string' ? g : (g?.logGroupName || g?.name || '')))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))
}

/**
 * Normalize backend response for the log-query endpoint.
 *
 * Canonical shape: { total: <number>, results: [ {...}, {...} ] }
 * Also tolerates bare arrays, Spring Page, and { items, total } wrappers.
 */
const normalizeQueryResponse = (raw) => {
  let items = []
  let total = null

  if (Array.isArray(raw)) {
    items = raw
  } else if (raw && typeof raw === 'object') {
    items = raw.results ?? raw.items ?? raw.content ?? raw.data ?? raw.events ?? []
    if (!Array.isArray(items)) items = []
    total = raw.total ?? raw.totalElements ?? raw.totalCount ?? null
  }

  const normalized = items.map((it, idx) => {
    const isoTimestamp = it?.['@timestamp'] ?? it?.timestamp ?? null
    const epochSecond = it?.epochSecond ?? it?.epochSpecond ?? null
    const logger = it?.logger ?? it?.loggerName ?? it?.logger_name ?? ''
    const thread = it?.thread ?? it?.threadName ?? it?.thread_name ?? ''
    const logStream = it?.['@logStream'] ?? it?.logStream ?? ''
    return {
      // stable id for v-data-table item-value / expansion
      id: `${isoTimestamp ?? epochSecond ?? 'na'}-${thread || 'na'}-${idx}`,
      epochSecond,
      isoTimestamp,
      timestamp: formatTs(isoTimestamp ?? epochSecond),
      level: it?.level ?? '',
      loggerName: logger,
      thread,
      message: it?.message ?? '',
      logStream,
      contextMap: it?.contextMap ?? {},
      type: it?.type ?? '',
      event: it?.event ?? null,
      metrics: it?.metrics ?? null,
      user: it?.user ?? null,
      vwg: it?.vwg ?? null,
      _raw: it,
    }
  })

  return { items: normalized, total }
}

/**
 * Preserve the raw row while mapping arbitrary CloudWatch column names to
 * table-safe keys. Vuetify treats dots in a header key as nested paths, so
 * fields such as `vwg.module.track_id` cannot be used as keys directly.
 */
const normalizeAnalyticsResponse = (raw) => {
  let rows = []
  let total = null

  if (Array.isArray(raw)) {
    rows = raw
  } else if (raw && typeof raw === 'object') {
    rows = raw.results ?? raw.items ?? raw.content ?? raw.data ?? []
    if (!Array.isArray(rows)) rows = []
    total = raw.total ?? raw.totalElements ?? raw.totalCount ?? null
  }

  const rawRows = rows.map((row) => (
    row && typeof row === 'object' && !Array.isArray(row) ? row : { value: row }
  ))
  const names = []
  const seen = new Set()
  rawRows.forEach((row) => {
    Object.keys(row).forEach((name) => {
      if (!seen.has(name)) {
        seen.add(name)
        names.push(name)
      }
    })
  })

  const columns = names.map((title, index) => ({
    title,
    key: `analyticsColumn${index}`,
  }))
  const items = rawRows.map((row, rowIndex) => {
    const item = {
      __rowId: `analytics-${analyticsPage.value}-${rowIndex}`,
      __raw: row,
    }
    columns.forEach(({ title, key }) => {
      item[key] = row[title]
    })
    return item
  })

  return { items, columns, total }
}

const loadLogGroups = async () => {
  logGroupsLoading.value = true
  try {
    const res = await dataInsightListLogGroups()
    logGroups.value = normalizeLogGroups(res)
    if (logGroups.value.length === 0) {
      toast.info('No CloudWatch log groups returned by backend')
    }
  } catch (err) {
    console.error('[data-insight] failed to load log groups', err)
    logGroups.value = []
  } finally {
    logGroupsLoading.value = false
  }
}

const buildSearchRequest = () => {
  const base = {
    startMs: toEpochMs(startDate.value),
    endMs: toEpochMs(endDate.value),
    logGroupNames: [...selectedLogGroups.value],
    pageNumber: page.value,
    pageSize: pageSize.value,
  }

  if (activeTab.value === 'keyword') {
    const kw = (keyword.value || '').trim()
    if (!kw) {
      toast.warning('Please enter a keyword')
      return null
    }
    return {
      mode: 'keyword',
      payload: { ...base, keyword: kw },
    }
  }

  return {
    mode: 'ql',
    payload: { ...base, queryFragment: (queryFragment.value || '').trim() },
  }
}

const runQuery = async () => {
  if (!selectedLogGroups.value || selectedLogGroups.value.length === 0) {
    toast.warning('Please select at least one Log Group')
    return
  }
  if (!startDate.value || !endDate.value) {
    toast.warning('Please select both Start Date and End Date')
    return
  }
  const startMs = toEpochMs(startDate.value)
  const endMs = toEpochMs(endDate.value)
  if (startMs == null || endMs == null || startMs >= endMs) {
    toast.warning('Start Date must be before End Date')
    return
  }
  const request = buildSearchRequest()
  if (request === null) return

  searchLoading.value = true
  try {
    const res = request.mode === 'keyword'
      ? await dataInsightSearchLogs(request.payload)
      : await dataInsightQueryLogs(request.payload)
    const { items, total } = normalizeQueryResponse(res)
    logItems.value = items
    // Backend may not return a total. Fall back to a sensible "we have at
    // least this many" so the table's pager still allows next-page probing
    // when the current page is full.
    if (total != null) {
      totalItems.value = total
    } else if (items.length === pageSize.value) {
      totalItems.value = page.value * pageSize.value + 1
    } else {
      totalItems.value = (page.value - 1) * pageSize.value + items.length
    }
    expanded.value = []
    visible.value = true

    if (items.length === 0 && page.value === 1) {
      toast.info('No log events found for the given filters')
    }
  } catch (err) {
    console.error('[data-insight] query failed', err)
    logItems.value = []
    totalItems.value = 0
    visible.value = true
  } finally {
    searchLoading.value = false
  }
}

const runAnalyticsQuery = async () => {
  if (!selectedLogGroups.value || selectedLogGroups.value.length === 0) {
    toast.warning('Please select at least one Log Group')
    return
  }
  if (!startDate.value || !endDate.value) {
    toast.warning('Please select both Start Date and End Date')
    return
  }
  const startMs = toEpochMs(startDate.value)
  const endMs = toEpochMs(endDate.value)
  if (startMs == null || endMs == null || startMs >= endMs) {
    toast.warning('Start Date must be before End Date')
    return
  }
  const query = (analyticsQuery.value || '').trim()
  if (!query) {
    toast.warning('Please enter a Log Analytics query')
    return
  }

  analyticsLoading.value = true
  try {
    const res = await dataInsightLogAnalytics({
      startMs,
      endMs,
      logGroupNames: [...selectedLogGroups.value],
      queryFragment: query,
      pageNumber: analyticsPage.value,
      pageSize: analyticsPageSize.value,
    })
    const { items, columns, total } = normalizeAnalyticsResponse(res)
    analyticsItems.value = items
    analyticsColumns.value = columns
    if (total != null) {
      analyticsTotalItems.value = Number(total) || 0
    } else if (items.length === analyticsPageSize.value) {
      analyticsTotalItems.value = analyticsPage.value * analyticsPageSize.value + 1
    } else {
      analyticsTotalItems.value = (analyticsPage.value - 1) * analyticsPageSize.value + items.length
    }
    analyticsExpanded.value = []
    analyticsVisible.value = true

    if (items.length === 0 && analyticsPage.value === 1) {
      toast.info('No analytics results found for the given query')
    }
  } catch (err) {
    console.error('[data-insight] log analytics query failed', err)
    analyticsItems.value = []
    analyticsColumns.value = []
    analyticsTotalItems.value = 0
    analyticsVisible.value = true
    toast.error(err?.response?.data?.message || 'Log Analytics query failed')
  } finally {
    analyticsLoading.value = false
  }
}

// "Search" button always resets to page 1, then runs.
const searchData = () => {
  if (activeTab.value === 'analytics') {
    if (analyticsPage.value !== 1) {
      analyticsPage.value = 1
      return
    }
    runAnalyticsQuery()
    return
  }
  if (page.value !== 1) {
    // Reset to page 1; the page watcher below will trigger the query.
    page.value = 1
    return
  }
  runQuery()
}

// React to user-driven pagination changes from the table footer.
//
// We deliberately use a watcher (instead of v-data-table-server's
// `@update:options`) because `v-model:page` writes the new value to
// `page` BEFORE `update:options` fires, so a diff check inside that
// handler is always false and the query never re-runs.
watch(page, () => {
  if (activeTab.value === 'analytics' || !visible.value) return
  runQuery()
})

watch(pageSize, () => {
  if (activeTab.value === 'analytics' || !visible.value) return
  if (page.value !== 1) {
    page.value = 1 // triggers the page watcher above
  } else {
    runQuery()
  }
})

watch(analyticsPage, () => {
  if (activeTab.value !== 'analytics' || !analyticsVisible.value) return
  runAnalyticsQuery()
})

watch(analyticsPageSize, () => {
  if (activeTab.value !== 'analytics' || !analyticsVisible.value) return
  if (analyticsPage.value !== 1) {
    analyticsPage.value = 1
  } else {
    runAnalyticsQuery()
  }
})

const clear = () => {
  selectedLogGroups.value = []
  startDate.value = toLocalInput(oneHourAgo)
  endDate.value = toLocalInput(now)
  activeTab.value = 'ql'
  queryFragment.value = DEFAULT_QL_FRAGMENT
  keyword.value = ''
  logItems.value = []
  totalItems.value = 0
  visible.value = false
  expanded.value = []
  page.value = 1
  pageSize.value = 25
  analyticsQuery.value = DEFAULT_ANALYTICS_QUERY
  analyticsItems.value = []
  analyticsColumns.value = []
  analyticsTotalItems.value = 0
  analyticsVisible.value = false
  analyticsExpanded.value = []
  analyticsPage.value = 1
  analyticsPageSize.value = 25
}

// Pretty-print any structured checkpoint or health-metric field.
const formatJson = (value) => {
  if (value == null) return ''
  if (typeof value !== 'object') return String(value)
  try {
    return JSON.stringify(value, null, 2)
  } catch (_) {
    return String(value)
  }
}

const hasValue = (value) => {
  if (value == null) return false
  if (typeof value === 'string') return value.trim().length > 0
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'object') return Object.keys(value).length > 0
  return true
}

const typeClass = (type) => {
  const normalizedType = String(type || '').toLowerCase()
  if (normalizedType === 'business-checkpoint') return 'type-checkpoint'
  if (normalizedType === 'business-health-metrics') return 'type-metrics'
  return 'type-generic'
}

const analyticsCellText = (value) => {
  if (value == null || value === '') return '—'
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch (_) {
      return String(value)
    }
  }
  return String(value)
}

const hasResults = computed(() => visible.value && logItems.value.length > 0)
const hasAnalyticsResults = computed(() => analyticsVisible.value && analyticsItems.value.length > 0)

onMounted(loadLogGroups)
</script>

<template>
  <v-container fluid class="page-shell">
    <v-row class="text-center mb-4">
      <v-col cols="12">
        <h1 class="title font-weight-bold">Data Insight</h1>
        <div class="headline-border" />
      </v-col>
    </v-row>

    <v-tabs v-model="activeTab" color="primary" class="data-insight-tabs mb-4">
      <v-tab value="ql">Logs Insights QL</v-tab>
      <v-tab value="keyword">Keyword</v-tab>
      <v-tab value="analytics">Log Analytics</v-tab>
    </v-tabs>

    <v-card class="section-card mb-4">
      <v-card-title>Search Filters</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="selectedLogGroups"
              :items="logGroups"
              :loading="logGroupsLoading"
              label="Log Groups"
              placeholder="Select one or more CloudWatch log groups"
              density="comfortable"
              multiple
              chips
              closable-chips
              clearable
              hide-no-data
              hide-details
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="startDate"
              type="datetime-local"
              label="Start Date (UTC)"
              density="comfortable"
              hide-details
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="endDate"
              type="datetime-local"
              label="End Date (UTC)"
              density="comfortable"
              hide-details
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col v-if="activeTab === 'ql'" cols="12">
            <v-textarea
              v-model="queryFragment"
              label="Query fragment (appended to a base Logs Insights query)"
              variant="outlined"
              density="comfortable"
              rows="4"
              auto-grow
              hide-details
              class="ql-textarea"
              spellcheck="false"
              placeholder="fields @timestamp, @logStream, @message&#10;| sort @timestamp desc"
            />
            <div class="hint-row">
              Backend appends this fragment to its base query. Leave empty to fetch everything in the time window.
              Add e.g. <code>| filter @message like /error/</code> to narrow results.
            </div>
          </v-col>
          <v-col v-else-if="activeTab === 'keyword'" cols="12">
            <v-text-field
              v-model="keyword"
              label="Keyword"
              placeholder="e.g. ERROR or request failed"
              density="comfortable"
              hide-details
              @keyup.enter="searchData"
            />
            <div class="hint-row">
              Sends a plain <code>keyword</code> field to <code>/insights/search</code>.
            </div>
          </v-col>
          <v-col v-else cols="12">
            <v-textarea
              v-model="analyticsQuery"
              label="CloudWatch Logs Insights query"
              variant="outlined"
              density="comfortable"
              rows="7"
              auto-grow
              hide-details
              class="ql-textarea"
              spellcheck="false"
              placeholder="fields @timestamp, @logStream, @message&#10;| stats count() by bin(1m)&#10;| sort @timestamp asc"
            />
            <div class="hint-row">
              Supports raw projections, <code>parse</code>, and <code>stats</code>. Result sizing is
              controlled by the selected page size (maximum 100).
            </div>
          </v-col>
        </v-row>
        <v-row class="mt-2">
          <v-col cols="12" class="d-flex align-center flex-wrap gap-3">
            <v-btn
              size="small"
              color="primary"
              :loading="activeTab === 'analytics' ? analyticsLoading : searchLoading"
              @click="searchData"
            >
              Search
            </v-btn>
            <v-btn
              size="small"
              variant="tonal"
              color="primary"
              :disabled="searchLoading || analyticsLoading"
              :loading="logGroupsLoading"
              @click="loadLogGroups"
            >
              Refresh Log Groups
            </v-btn>
            <v-btn
              size="small"
              variant="text"
              color="secondary"
              @click="clear"
            >
              Clear
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <template v-if="activeTab !== 'analytics' && visible">
      <v-card class="section-card">
        <v-card-title class="info-title">
          CloudWatch Log Events
          <span class="result-count">
            ({{ totalItems }} total<span v-if="hasResults">, page {{ page }}</span>)
          </span>
        </v-card-title>
        <v-data-table-server
          v-model:expanded="expanded"
          v-model:page="page"
          v-model:items-per-page="pageSize"
          :headers="headers"
          :items="logItems"
          :items-length="totalItems"
          :loading="searchLoading"
          :items-per-page-options="[10, 25, 50, 100]"
          density="compact"
          item-value="id"
          show-expand
          class="elevation-1 monitoring-table"
        >
          <template #item.level="{ item }">
            <span class="level-badge" :class="levelClass(item.level)">{{ item.level || '—' }}</span>
          </template>
          <template #item.message="{ item }">
            <div class="msg-cell" :title="item.message">{{ item.message }}</div>
          </template>
          <template #expanded-row="{ item, columns }">
            <tr class="expanded-row">
              <td :colspan="columns.length" class="pa-3">
                <div v-if="hasValue(item.type) || hasValue(item.isoTimestamp)" class="expanded-meta">
                  <span v-if="hasValue(item.type)" class="type-badge" :class="typeClass(item.type)">
                    {{ item.type }}
                  </span>
                  <span v-if="hasValue(item.isoTimestamp)" class="meta-ts">
                    <span class="meta-key">@timestamp:</span> {{ item.isoTimestamp }}
                  </span>
                </div>
                <div class="expanded-grid">
                  <div class="expanded-block">
                    <div class="expanded-label">
                      Message
                      <v-btn class="copy-btn" size="x-small" variant="text" @click="copyToClipboard(item.message)">Copy</v-btn>
                    </div>
                    <pre class="msg-full">{{ item.message }}</pre>
                  </div>
                  <div v-if="hasValue(item.logStream)" class="expanded-block">
                    <div class="expanded-label">
                      Log Stream
                      <v-btn class="copy-btn" size="x-small" variant="text" @click="copyToClipboard(item.logStream)">Copy</v-btn>
                    </div>
                    <pre class="msg-full">{{ item.logStream }}</pre>
                  </div>
                  <div v-if="hasValue(item.contextMap)" class="expanded-block">
                    <div class="expanded-label">
                      Context
                      <v-btn class="copy-btn" size="x-small" variant="text" @click="copyToClipboard(formatJson(item.contextMap))">Copy</v-btn>
                    </div>
                    <pre class="msg-full">{{ formatJson(item.contextMap) }}</pre>
                  </div>
                  <div v-if="hasValue(item.vwg)" class="expanded-block">
                    <div class="expanded-label">
                      VWG
                      <v-btn class="copy-btn" size="x-small" variant="text" @click="copyToClipboard(formatJson(item.vwg))">Copy</v-btn>
                    </div>
                    <pre class="msg-full">{{ formatJson(item.vwg) }}</pre>
                  </div>
                  <div v-if="hasValue(item.metrics)" class="expanded-block">
                    <div class="expanded-label">
                      Metrics
                      <v-btn class="copy-btn" size="x-small" variant="text" @click="copyToClipboard(formatJson(item.metrics))">Copy</v-btn>
                    </div>
                    <pre class="msg-full">{{ formatJson(item.metrics) }}</pre>
                  </div>
                  <div v-if="hasValue(item.event)" class="expanded-block">
                    <div class="expanded-label">
                      Event
                      <v-btn class="copy-btn" size="x-small" variant="text" @click="copyToClipboard(formatJson(item.event))">Copy</v-btn>
                    </div>
                    <pre class="msg-full">{{ formatJson(item.event) }}</pre>
                  </div>
                  <div v-if="hasValue(item.user)" class="expanded-block">
                    <div class="expanded-label">
                      User
                      <v-btn class="copy-btn" size="x-small" variant="text" @click="copyToClipboard(formatJson(item.user))">Copy</v-btn>
                    </div>
                    <pre class="msg-full">{{ formatJson(item.user) }}</pre>
                  </div>
                </div>
              </td>
            </tr>
          </template>
          <template #no-data>
            <div class="empty-state">No results. Adjust filters and click Search.</div>
          </template>
        </v-data-table-server>
      </v-card>
    </template>

    <template v-if="activeTab === 'analytics' && analyticsVisible">
      <v-card class="section-card">
        <v-card-title class="info-title">
          Log Analytics Results
          <span class="result-count">
            ({{ analyticsTotalItems }} total<span v-if="hasAnalyticsResults">, page {{ analyticsPage }}</span>)
          </span>
        </v-card-title>
        <v-data-table-server
          v-model:expanded="analyticsExpanded"
          v-model:page="analyticsPage"
          v-model:items-per-page="analyticsPageSize"
          :headers="analyticsHeaders"
          :items="analyticsItems"
          :items-length="analyticsTotalItems"
          :loading="analyticsLoading"
          :items-per-page-options="[10, 25, 50, 100]"
          density="compact"
          item-value="__rowId"
          show-expand
          class="elevation-1 monitoring-table analytics-table"
        >
          <template
            v-for="column in analyticsColumns"
            :key="column.key"
            #[`item.${column.key}`]="{ item }"
          >
            <div class="analytics-cell" :title="analyticsCellText(item[column.key])">
              {{ analyticsCellText(item[column.key]) }}
            </div>
          </template>
          <template #expanded-row="{ item, columns }">
            <tr class="expanded-row">
              <td :colspan="columns.length" class="pa-3">
                <div class="expanded-label">
                  Raw CloudWatch Row
                  <v-btn
                    class="copy-btn"
                    size="x-small"
                    variant="text"
                    @click="copyToClipboard(formatJson(item.__raw))"
                  >
                    Copy
                  </v-btn>
                </div>
                <pre class="msg-full">{{ formatJson(item.__raw) }}</pre>
              </td>
            </tr>
          </template>
          <template #no-data>
            <div class="empty-state">No results. Adjust the query and click Search.</div>
          </template>
        </v-data-table-server>
      </v-card>
    </template>
  </v-container>
</template>

<style scoped>
.page-shell { padding: 8px 4px; }

.title {
  font-size: 1.6rem;
  letter-spacing: 0.3px;
  margin: 0;
}

.headline-border {
  width: 80px;
  height: 4px;
  background: #2563eb;
  border-radius: 2px;
  margin: 8px auto 0;
}

.section-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.05);
}

.data-insight-tabs {
  border-bottom: 1px solid #e2e8f0;
}

.info-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.result-count {
  color: #64748b;
  font-size: 0.9rem;
  font-weight: 500;
}

.gap-3 { gap: 12px; }

.monitoring-table { border-radius: 8px; }

.msg-cell {
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  cursor: default;
}

.analytics-cell {
  max-width: 420px;
  overflow: hidden;
  color: #334155;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.analytics-table { overflow-x: auto; }

.msg-full {
  margin: 0;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 10px 12px;
  max-height: 360px;
  overflow: auto;
}

.expanded-row td { background: #f1f5f9; }

.expanded-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

@media (max-width: 900px) {
  .expanded-grid { grid-template-columns: 1fr; }
}

.expanded-label {
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 4px;
}

.copy-btn {
  margin-left: auto;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
}

.expanded-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 14px;
  margin-bottom: 10px;
}

.meta-ts {
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  color: #334155;
}

.meta-key {
  color: #64748b;
  font-weight: 600;
  margin-right: 4px;
}

.type-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.4px;
  background: #e2e8f0;
  color: #334155;
}

.type-badge.type-checkpoint { background: #dcfce7; color: #166534; }
.type-badge.type-metrics { background: #e0f2fe; color: #075985; }
.type-badge.type-generic { background: #f1f5f9; color: #475569; }

.empty-state {
  padding: 32px 12px;
  text-align: center;
  color: #64748b;
}

.ql-textarea :deep(textarea) {
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace;
  font-size: 13px;
  line-height: 1.5;
}

.hint-row {
  margin-top: 6px;
  color: #64748b;
  font-size: 0.78rem;
}

.hint-row code {
  background: #f1f5f9;
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 0.78rem;
}

.level-badge {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.4px;
  background: #e2e8f0;
  color: #334155;
  min-width: 52px;
  text-align: center;
}

.level-badge.level-error { background: #fee2e2; color: #991b1b; }
.level-badge.level-warn  { background: #fef3c7; color: #92400e; }
.level-badge.level-info  { background: #dbeafe; color: #1e40af; }
.level-badge.level-debug { background: #ede9fe; color: #5b21b6; }
</style>

